import { useState, useRef } from "react";
import {
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Box,
  Avatar,
  Dialog,
  Tooltip,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import type {
  AccountKind,
  Entity,
  NewEntity,
  EntityStyle,
  LogoKind,
} from "../../../api/types";
import { LogoEditor } from "./LogoEditor";
import { ColorField } from "./ColorField";
import { logoUrl } from "../../../api/logo";

export interface EntityFormProps<Id> {
  initialData?: Entity<Id, string> | null;
  defaultName?: string;
  onSave: (
    entity: NewEntity<string>,
    logoFile?: Blob | null,
    resetLogo?: boolean,
  ) => void;
  onCancel: () => void;
  entityType: LogoKind;
}

// Colours start unset: an entity without them gets a generated disc in a hue
// hashed from its name (backend `utils/svg.rs`). Filling white/black here
// stored them on every create and hid that hue for good.
const DEFAULT_ENTITY: NewEntity<string> = {
  name: "",
  description: "",
  colorBg: null,
  colorFg: null,
  style: "normal",
  priority: 0,
  hidden: false,
};

/** Label for each account kind, in the order the picker offers them. */
const ACCOUNT_KINDS: { value: AccountKind; label: string }[] = [
  { value: "checking", label: "Checking account" },
  { value: "savings", label: "Savings account" },
  { value: "investment", label: "Investment account" },
  { value: "creditCard", label: "Credit card" },
  { value: "loan", label: "Loan" },
];

export const EntityForm = <Id extends string>({
  initialData,
  defaultName,
  onSave,
  onCancel,
  entityType,
}: EntityFormProps<Id>) => {
  const [isLogoPreviewOpen, setIsLogoPreviewOpen] = useState(false);
  // Only the source registry holds accounts; the label registries (group,
  // category, expense type, payee) have neither column. The backend would
  // drop the values, but the form doesn't send them: a label's request body
  // carries only the label's own fields.
  const isAccount = entityType === "source";
  const [formData, setFormData] = useState<NewEntity<string>>(
    initialData
      ? {
          name: initialData.name,
          description: initialData.description || "",
          // Kept as stored — null stays null, so saving an unrelated change
          // (toggling Hidden, say) doesn't introduce colours.
          colorBg: initialData.colorBg || null,
          colorFg: initialData.colorFg || null,
          style: initialData.style || "normal",
          priority: initialData.priority || 0,
          hidden: initialData.hidden || false,
          ...(isAccount && {
            accountNumber: initialData.accountNumber ?? null,
            kind: initialData.kind ?? null,
          }),
        }
      : { ...DEFAULT_ENTITY, name: defaultName ?? "" },
  );

  // Logo state
  const [logoFile, setLogoFile] = useState<Blob | null>(null);
  const [resetLogo, setResetLogo] = useState(false);
  const [editorFile, setEditorFile] = useState<File | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  // Bumped on every file selection so the keyed LogoEditor remounts with fresh
  // state — even when re-selecting the same file.
  const [editorSession, setEditorSession] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleChange = (
    field: keyof NewEntity<string>,
    // `null` clears an optional field: an account number left blank, or a kind
    // set back to "not classified". Sending `""` would store an empty string
    // where the column means "unknown".
    value: string | number | boolean | AccountKind | null,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setEditorFile(e.target.files[0]);
      setIsEditorOpen(true);
      setEditorSession((n) => n + 1);
    }
    // Reset input so same file can be selected again
    e.target.value = "";
  };

  const handleLogoSave = (blob: Blob) => {
    setLogoFile(blob);
    setResetLogo(false);
    setIsEditorOpen(false);
    setEditorFile(null);
  };

  const handleLogoReset = () => {
    setLogoFile(null);
    // In add mode there is nothing on the server yet — only clear the local
    // selection. resetLogo is the "delete the saved logo on save" signal and
    // only applies when editing an existing entity.
    if (initialData) setResetLogo(true);
  };

  const logoSrc = logoFile
    ? URL.createObjectURL(logoFile)
    : initialData && !resetLogo
      ? logoUrl(entityType, initialData)
      : undefined;

  return (
    <>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={logoSrc}
                sx={{
                  width: 100,
                  height: 100,
                  cursor: logoSrc ? "pointer" : "default",
                  border: "1px solid #ccc",
                  bgcolor: formData.colorBg || "transparent",
                  color: formData.colorFg || "inherit",
                }}
                onClick={() => logoSrc && setIsLogoPreviewOpen(true)}
              >
                {!logoSrc && formData.name.slice(0, 2).toUpperCase()}
              </Avatar>

              {/* Logo Controls — available in both add and edit mode. In add
                  mode the chosen file is uploaded after the entity is created
                  (EntityList.handleSave handles the two-step flow). */}
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  position: "absolute",
                  bottom: -10,
                  right: -40,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  boxShadow: 1,
                  p: 0.5,
                }}
              >
                <Tooltip title="Upload Logo">
                  <IconButton
                    size="small"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={initialData ? "Reset to Default" : "Clear"}>
                  {/* span wrapper keeps the tooltip working when the button is
                      disabled (MUI requirement). */}
                  <span>
                    <IconButton
                      size="small"
                      onClick={handleLogoReset}
                      // Nothing to clear when no logo is showing — either no
                      // file selected (add mode) or already reset (edit mode).
                      disabled={!logoSrc}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Box>
            <input
              type="file"
              accept=".svg,image/*"
              hidden
              ref={fileInputRef}
              onChange={handleFileSelect}
            />
          </Box>

          <TextField
            label="Name"
            fullWidth
            required
            value={formData.name}
            onChange={(e) => handleChange("name", e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
          />

          {isAccount && (
            <Stack direction="row" spacing={2}>
              <TextField
                label="Account number"
                fullWidth
                value={formData.accountNumber ?? ""}
                placeholder="1225.88.74453"
                helperText="Leave blank if the account has none — a card billed through the issuer's shared account, for instance."
                onChange={(e) =>
                  handleChange("accountNumber", e.target.value.trim() || null)
                }
              />
              <FormControl fullWidth>
                <InputLabel id="account-kind-label">Kind</InputLabel>
                <Select
                  labelId="account-kind-label"
                  label="Kind"
                  value={formData.kind ?? ""}
                  onChange={(e) =>
                    handleChange(
                      "kind",
                      (e.target.value as AccountKind) || null,
                    )
                  }
                >
                  <MenuItem value="">
                    <em>Not classified</em>
                  </MenuItem>
                  {ACCOUNT_KINDS.map(({ value, label }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          )}

          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: "center",
            }}
          >
            <ColorField
              label="Background Color"
              value={formData.colorBg ?? null}
              pickerStart="#ffffff"
              onChange={(c) => handleChange("colorBg", c)}
            />
            <ColorField
              label="Foreground Color"
              value={formData.colorFg ?? null}
              pickerStart="#000000"
              onChange={(c) => handleChange("colorFg", c)}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Style</InputLabel>
              <Select
                value={formData.style || "normal"}
                label="Style"
                onChange={(e) =>
                  handleChange("style", e.target.value as EntityStyle)
                }
              >
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="bold">Bold</MenuItem>
                <MenuItem value="italic">Italic</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Priority"
              type="number"
              fullWidth
              value={formData.priority}
              onChange={(e) =>
                handleChange("priority", parseInt(e.target.value) || 0)
              }
            />
          </Stack>

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.hidden || false}
                onChange={(e) => handleChange("hidden", e.target.checked)}
              />
            }
            label="Hidden"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          onClick={() => onSave(formData, logoFile, resetLogo)}
          variant="contained"
          disabled={!formData.name}
        >
          Save
        </Button>
      </DialogActions>
      {/* Full Image Preview */}
      {logoSrc && (
        <Dialog
          open={isLogoPreviewOpen}
          onClose={() => setIsLogoPreviewOpen(false)}
          maxWidth="md"
        >
          <Box
            component="img"
            src={logoSrc}
            alt={initialData?.name || formData.name}
            sx={{
              width: "100%",
              height: "auto",
              maxHeight: "80vh",
              objectFit: "contain",
              p: 2,
              bgcolor: "background.paper",
            }}
            onClick={() => setIsLogoPreviewOpen(false)}
          />
        </Dialog>
      )}
      {/* Logo Editor — keyed per selection so a new file remounts it with
          fresh state instead of resetting via an effect. */}
      <LogoEditor
        key={editorSession}
        open={isEditorOpen}
        file={editorFile}
        onClose={() => {
          setIsEditorOpen(false);
          setEditorFile(null);
        }}
        onSave={handleLogoSave}
      />
    </>
  );
};
